import * as React from 'react';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Avatar, CircularProgress, Divider, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../../services/api';
import { useEvent } from '../../hooks/useEvent';

export default function ContactAutoComplete({ placeholder, cardId, value, onChange }) {
    const theme = useTheme();
    const emitter = useEvent();
    const isControlled = value !== undefined && typeof onChange === 'function';
    const [searchParam, setSearchParam] = React.useState('');
    const [contacts, setContacts] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [internalContact, setInternalContact] = React.useState(null);

    const selectedContact = isControlled ? value : internalContact;
    const setSelectedContact = isControlled
        ? (next) => onChange(next || null)
        : setInternalContact;

    const debounceTimeout = React.useRef(null);
    const abortController = React.useRef(null);

    React.useEffect(() => {
        if (isControlled) return;
        const fetchCardContact = async () => {
            if (!cardId) return;
            setLoading(true);
            try {
                const { data } = await api.get(`/kanban/card/requester/${cardId}`);
                if (data?.requester) {
                    setContacts([data.requester]);
                    setInternalContact(data.requester);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchCardContact();
    }, [cardId, isControlled]);

    React.useEffect(() => {
        if (isControlled && value && !contacts.find((c) => c.id === value.id)) {
            setContacts((prev) => [value, ...prev]);
        }
    }, [isControlled, value, contacts]);

    // Limpar ao desmontar
    React.useEffect(() => {
        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
            if (abortController.current) abortController.current.abort();
        };
    }, []);

    const handleSearchContact = async (value) => {
        if (!value || value.length < 2) {
            setContacts(selectedContact ? [selectedContact] : []);
            return;
        }

        // Cancelar requisição anterior
        if (abortController.current) {
            abortController.current.abort();
        }
        abortController.current = new AbortController();

        setLoading(true);
        try {
            const { data } = await api.get('/listContacts', {
                params: { searchParam: value.toLowerCase() },
                signal: abortController.current.signal
            });
            setContacts(data?.contacts || []);
        } catch (error) {
            if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
                console.error(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event, newValue) => {
        setSearchParam(newValue);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            handleSearchContact(newValue);
        }, 400);
    };

    const handleSelectContact = async (contact) => {
        if (!contact) {
            setSelectedContact(null);
            return;
        }

        setSelectedContact(contact);

        if (!isControlled && cardId) {
            try {
                setSaving(true);
                const { data } = await api.put(`/kanban/card/${cardId}`, {
                    requesterId: contact.id
                });
                emitter.emit("kanban-card", {
                    action: 'update',
                    kanbanId: data.kanbanId,
                    columnId: data.kanbanColumnId,
                    cardId: cardId,
                    card: data
                });
            } catch (error) {
                console.error('Error updating card contact:', error);
            } finally {
                setSaving(false);
            }
        }
    };

    const handleClearSelection = async () => {
        if (isControlled) {
            setSelectedContact(null);
            setSearchParam('');
            return;
        }
        if (cardId) {
            try {
                setSaving(true);
                const { data } = await api.put(`/kanban/card/${cardId}`, {
                    requesterId: null
                });
                emitter.emit("kanban-card", {
                    action: 'update',
                    kanbanId: data.kanbanId,
                    columnId: data.kanbanColumnId,
                    cardId: cardId,
                    card: data
                });
                setInternalContact(null);
                setSearchParam('');
                setContacts([]);
            } catch (error) {
                console.error('Error updating card contact:', error);
            } finally {
                setSaving(false);
            }
        }
    };

    const getSecondaryText = (option) => {
        if (option.isGroup) return 'Grupo';
        const parts = [];
        if (option.number) parts.push(`+${option.number}`);
        if (option.email) parts.push(option.email);
        if (option.contract) parts.push(`#${option.contract}`);
        return parts.join(' | ') || '';
    };

    const noOptionsLabel = React.useMemo(() => {
        if (loading) return 'Buscando...';
        if (searchParam.length > 0 && searchParam.length < 2) return 'Digite ao menos 2 caracteres...';
        if (searchParam.length >= 2 && contacts.length === 0) return 'Nenhum contato encontrado';
        return 'Digite para pesquisar por nome, telefone, email ou contrato...';
    }, [loading, searchParam, contacts.length]);

    return (
        <Autocomplete
            size="small"
            id="contact-autocomplete"
            sx={{ width: '100%' }}
            options={contacts}
            autoHighlight
            value={selectedContact}
            inputValue={searchParam}
            onInputChange={handleInputChange}
            onChange={(event, newValue) => handleSelectContact(newValue)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => option ? `${option.name}` : ''}
            filterOptions={(x) => x}
            noOptionsText={noOptionsLabel}
            loading={loading}
            renderOption={(props, option) => (
                <Box
                    component="li"
                    key={option.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}
                    {...props}
                >
                    <Avatar
                        src={option.profilePicUrl}
                        sx={{ width: 32, height: 32, flexShrink: 0 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                            {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {getSecondaryText(option)}
                        </Typography>
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    sx={{ '& .MuiOutlinedInput-root': { padding: '4px 5px 4px 6px !important' } }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <Typography sx={{ marginLeft: '10px', fontSize: '0.75rem', fontWeight: 600, color: theme.palette.text.secondary }}>
                                    {placeholder}
                                </Typography>
                                <Divider sx={{ margin: '0px 10px' }} orientation="vertical" flexItem />
                            </>
                        ),
                        endAdornment: (
                            <>
                                {(loading || saving) ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {selectedContact && (
                                            <IconButton onClick={handleClearSelection} sx={{ padding: 0 }}>
                                                <ClearIcon sx={{ color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000' }} />
                                            </IconButton>
                                        )}
                                        <AddIcon />
                                    </Box>
                                )}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
